//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
// import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
// import IconButton from '@material-ui/core/IconButton';
// import ButtonGroup from '@material-ui/core/ButtonGroup';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { LayoutContext } from '../../common/contexts';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
// import { getStateLabelByCountryName } from '../../common/locationHelper';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
import {
    PLACEHOLDER_FIELD_FIRST_NAME,
    PLACEHOLDER_FIELD_MIDDLE_NAME,
    PLACEHOLDER_FIELD_LAST_NAME,
    // PLACEHOLDER_FIELD_COUNTRY,
    // PLACEHOLDER_FIELD_STATE,
    // PLACEHOLDER_FIELD_CITY,
    PLACEHOLDER_FIELD_EMPLOYMENT_TYPE,
    PLACEHOLDER_FIELD_OCCUPATION,
    PLACEHOLDER_FIELD_SKILLSET,
    PLACEHOLDER_FIELD_EDUCATION,
    PLACEHOLDER_FIELD_OTHER_EDUCATION,
    PLACEHOLDER_FIELD_ABOUT_ME,
    // getStateLovPlaceholder,
    // getCityLovPlaceholder,
    PLACEHOLDER_FIELD_PAY_FREQUENCY,
    PLACEHOLDER_FIELD_GENDER,
    PLACEHOLDER_FIELD_RACE,
    NO_OPTIONS_TEXT_OCCUPATION,
    NO_OPTIONS_TEXT_SKILLSET,
    NO_OPTIONS_TEXT_OTHER_EDUCATION,
    NO_OPTIONS_TEXT_GENDER,
} from '../../common/placeholderHelper';
import { getCreatableLovSuggestions } from '../../common/lovHelper';
import { ELASTICSEARCH_TYPE_OCCUPATION, ELASTICSEARCH_TYPE_SKILL_SET, ELASTICSEARCH_TYPE_OTHER_EDUCATION } from '../../common/elasticSearchHelper';
import { SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED } from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveProfileAboutMe } from '../../common/services/myProfileServices';
import { moderateUserTextInput } from '../../common/sightEngineHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdButtonGroup from '../../common/elemental/AfitdButtonGroup';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdAutocompleteLite from '../../common/elemental/AfitdAutocompleteLite';
import AfitdAutocompleteLiteCreatable from '../../common/elemental/AfitdAutocompleteLiteCreatable';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import AfitdStateAutocomplete from '../../common/elemental/AfitdStateAutocomplete';
import AfitdCityAutocomplete from '../../common/elemental/AfitdCityAutocomplete';
import AfitdSalaryRate from '../../common/elemental/AfitdSalaryRate';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdCurrencyAutocomplete from '../../common/elemental/AfitdCurrencyAutocomplete';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdYearsOfExperienceAutocomplete from '../../common/elemental/AfitdYearsOfExperienceAutocomplete';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import DialogFieldLabel from './DialogFieldLabel';
import DialogFieldLabelWithTrackStateInfo from './DialogFieldLabelWithTrackStateInfo';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import { format } from 'date-fns';
import _get from 'lodash/get';
// import _set from 'lodash/set';
import _isNil from 'lodash/isNil';
import _some from 'lodash/some';
import _forEach from 'lodash/forEach';
import _keys from 'lodash/keys';
import _includes from 'lodash/includes';
import _remove from 'lodash/remove';
import _isEmpty from 'lodash/isEmpty';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { willingRelocate } = params;

    const willingRelocateButtonStyles = {
        paddingLeft: 20,
        paddingRight: 20,
        background: '#1D8A8B',
        color: '#FFF',
        // textTransform: 'none',
        '&:hover': {
            backgroundColor: '#E0E1E5',
            color: '#000',
        },
    };

    const willingRelocateButtonOffStyles = {
        paddingLeft: 20,
        paddingRight: 20,
        background: 'rgba(0, 0, 0, 0.03)',
        color: '#111E14',
    };

    return makeStyles(theme => ({
        root: {},
        dialogContentContainer: {
            minHeight: '200px',
        },
        fieldContainer: {
            flex: 1,
            margin: '0 5px',
            marginBottom: '24px',
        },
        flexFieldContainer: {
            display: 'flex',
        },
        lastFieldContainer: {
            marginBottom: '62px',
        },
        hidden: {
            display: 'none',
        },
        willingRelocateButtonGroup: {
            '& :not(:last-child)': {
                borderRight: 'none',
            },
        },
        willingRelocateNoButton: willingRelocate === false ? willingRelocateButtonStyles : willingRelocateButtonOffStyles,
        willingRelocateYesButton: willingRelocate === true ? willingRelocateButtonStyles : willingRelocateButtonOffStyles,
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        appContext,
        isDirty,
        setIsDirty,
        closeDialog,
        clearFields,
        isConfirmClearAllDialogOpen,
        setIsConfirmClearAllDialogOpen,
        lovs,
        // countryLov,
        // stateLov,
        // cityLov,
        firstName,
        setFirstName,
        middleName,
        setMiddleName,
        lastName,
        setLastName,
        gender,
        setGender,
        race,
        setRace,
        country,
        // setCountry,
        state,
        // setState,
        city,
        // setCity,
        // cityLocation,
        // setCityLocation,
        countryCurrency,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
        employmentType,
        setEmploymentType,
        yearsOfExperience,
        setYearsOfExperience,
        isExactRate,
        setIsExactRate,
        rateMin,
        setRateMin,
        rateMax,
        setRateMax,
        currency,
        setCurrency,
        currencySymbol,
        setCurrencySymbol,
        payFrequency,
        setPayFrequency,
        occupation,
        setOccupation,
        skillset,
        setSkillset,
        education,
        setEducation,
        otherEducation,
        setOtherEducation,
        aboutMe,
        setAboutMe,
        // willingRelocate,
        setWillingRelocate,
        handleClickSaveButton,
        setSalaryRateIsValid,
        //getPageIsValid,

        invalidRequiredFields,
        setInvalidRequiredFields,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,

        occupationOptions,
        setOccupationOptions,
        skillsetOptions,
        setSkillsetOptions,
        otherEducationOptions,
        setOtherEducationOptions,
        openBadWordsDialog,
        badWords,
        clearOutBadWords,
        saveLoading,
    } = params;

    const { open } = props;
    // const { hidden: hiddenCls, fieldContainer: fieldContainerCls } = classes;

    // const otherEducationContainerIsHidden = _toLower(_get(education, 'value')) !== 'other';

    const { esToken, setEsToken } = appContext;

    const genderLov = _get(lovs, 'genderLov', []);
    const raceLov = _get(lovs, 'raceLov', []);
    const payFrequencyLov = _get(lovs, 'payFrequencyLov', []);
    // const occupationLov = _get(lovs, 'occupationLov', []);
    // const skillsetLov = _get(lovs, 'skillsetLov', []);
    const employmentTypeLov = _get(lovs, 'employmentTypeLov', []);
    const educationLov = _get(lovs, 'educationLov', []);
    // const otherEducationLov = _get(lovs, 'otherEducationLov', []);

    const badWordsList = badWords?.profanity?.join(', ');

    // const stateDisabled = !country;
    // const cityDisabled = !state || !country;
    // const stateLabelValue = getStateLabelByCountryName(country);

    // const statePlaceholder = stateDisabled ? '' : getStateLovPlaceholder(stateLov, PLACEHOLDER_FIELD_STATE);
    // const cityPlaceholder = cityDisabled ? '' : getCityLovPlaceholder(cityLov, PLACEHOLDER_FIELD_CITY);

    const defns = {
        root: {
            label: 'About Me',
            open,
            isDirty,
            onClose: handleCloseDialog.bind(null, closeDialog),
            actionButtonDefns: {
                clearAll: {
                    disabled: isFormEmpty([
                        firstName,
                        middleName,
                        lastName,
                        gender,
                        race,
                        country,
                        state,
                        city,
                        employmentType,
                        isExactRate,
                        rateMin,
                        rateMax,
                        occupation,
                        skillset,
                        education,
                        otherEducation,
                        aboutMe,
                    ]),
                    onClick: handleClickClearAllButton.bind(null, setIsConfirmClearAllDialogOpen),
                },
                save: {
                    disabled: !isDirty, // || !getPageIsValid(),
                    onClick: handleClickSaveButton.bind(null),
                    label: saveLoading ? (
                        <>
                            <CircularProgress color="#ffffff" size={20} />
                            &nbsp; Saving
                        </>
                    ) : undefined,
                },
            },
        },
        confirmClearAllDialog: {
            open: isConfirmClearAllDialogOpen,
            onClose: handleCloseConfirmClearAllDialog.bind(null, setIsConfirmClearAllDialogOpen),
        },
        confirmClearAllDialogCancelButton: {
            onClick: handleCloseConfirmClearAllDialog.bind(null, setIsConfirmClearAllDialogOpen),
        },
        confirmClearAllDialogOkButton: {
            autoFocus: true,
            onClick: handleClickConfirmClearAllDialogYes.bind(null, clearFields, setIsDirty, setIsConfirmClearAllDialogOpen),
        },
        firstName: {
            placeholder: PLACEHOLDER_FIELD_FIRST_NAME,
            inputProps: { maxLength: 100 },
            value: firstName,
            error: _includes(invalidRequiredFields, 'firstName'),
            onChange: handleChangeField.bind(null, { set: setFirstName, setIsDirty, name: 'firstName', invalidRequiredFields, setInvalidRequiredFields }),
        },
        middleName: {
            placeholder: PLACEHOLDER_FIELD_MIDDLE_NAME,
            inputProps: { maxLength: 100 },
            value: middleName,
            onChange: handleChangeField.bind(null, { set: setMiddleName, setIsDirty }),
        },
        lastName: {
            placeholder: PLACEHOLDER_FIELD_LAST_NAME,
            inputProps: { maxLength: 100 },
            value: lastName,
            error: _includes(invalidRequiredFields, 'lastName'),
            onChange: handleChangeField.bind(null, { set: setLastName, setIsDirty, name: 'lastName', invalidRequiredFields, setInvalidRequiredFields }),
        },
        gender: {
            noOptionsText: NO_OPTIONS_TEXT_GENDER,
            options: genderLov,
            placeholder: PLACEHOLDER_FIELD_GENDER,
            value: gender,
            error: _includes(invalidRequiredFields, 'gender'),
            onChange: handleChangeLovField.bind(null, { set: setGender, setIsDirty, name: 'gender', invalidRequiredFields, setInvalidRequiredFields }),
        },
        race: {
            options: raceLov,
            placeholder: PLACEHOLDER_FIELD_RACE,
            value: race,
            error: _includes(invalidRequiredFields, 'race'),
            onChange: handleChangeLovField.bind(null, { set: setRace, setIsDirty, name: 'race', invalidRequiredFields, setInvalidRequiredFields }),
        },
        country: {
            // options: countryLov,
            // placeholder: PLACEHOLDER_FIELD_COUNTRY,
            // value: country,
            // onChange: handleChangeCountry.bind(null, setCountry, setIsDirty, setState, setCity, setCityLocation),
            ...countryProps,
            error: !!_includes(invalidRequiredFields, 'country'),
        },
        stateLabel: {
            // value: stateLabelValue,
            ...stateLabelProps,
        },
        state: {
            // options: stateLov,
            // placeholder: statePlaceholder,
            // value: state,
            // onChange: handleChangeState.bind(null, setState, setIsDirty, setCity, setCityLocation),
            // disabled: stateDisabled,
            ...stateProps,
            error: !!_includes(invalidRequiredFields, 'state'),
        },
        city: {
            // isCapitalize: true,
            // noOptionsText: 'Type and select city',
            // options: cityLov,
            // placeholder: cityPlaceholder,
            // value: city,
            // onChange: handleChangeCity.bind(null, setCity, setIsDirty),
            // disabled: cityDisabled,
            ...cityProps,
            error: !!_includes(invalidRequiredFields, 'city'),
        },
        employmentType: {
            options: employmentTypeLov,
            placeholder: PLACEHOLDER_FIELD_EMPLOYMENT_TYPE,
            isMulti: true,
            value: employmentType,
            onChange: handleChangeMultiLovField.bind(null, { set: setEmploymentType, setIsDirty }),
        },
        yearsOfExperience: {
            // options: yearsOfExperienceLov,
            // placeholder: PLACEHOLDER_FIELD_YEARS_OF_EXPERIENCE,
            value: yearsOfExperience,
            onChange: handleChangeYearsOfExperience.bind(null, setYearsOfExperience, setIsDirty),
            error: _includes(invalidRequiredFields, 'yearsOfExperience'),
        },
        salaryRate: {
            isExactRate,
            rateMin,
            rateMax,
            currencySymbol,
            onChange: handleChangeSalaryRate.bind(null, setIsExactRate, setRateMin, setRateMax, setIsDirty),
            setIsValid: setSalaryRateIsValid,
        },
        currency: {
            value: currency,
            error: _includes(invalidRequiredFields, 'currency'),
            onChange: handleChangeCurrency.bind(null, {
                set: setCurrency,
                setSymbol: setCurrencySymbol,
                setIsDirty,
                name: 'currency',
                invalidRequiredFields,
                setInvalidRequiredFields,
            }),
            countryCurrency,
        },
        payFrequency: {
            options: payFrequencyLov,
            placeholder: PLACEHOLDER_FIELD_PAY_FREQUENCY,
            value: payFrequency,
            error: _includes(invalidRequiredFields, 'payFrequency'),
            onChange: handleChangeLovField.bind(null, { set: setPayFrequency, setIsDirty, name: 'payFrequency', invalidRequiredFields, setInvalidRequiredFields }),
        },
        occupation: {
            noOptionsText: NO_OPTIONS_TEXT_OCCUPATION,
            options: occupationOptions,
            placeholder: PLACEHOLDER_FIELD_OCCUPATION,
            isMulti: true,
            value: occupation,
            error: _includes(invalidRequiredFields, 'occupation'),
            onChange: handleChangeMultiLovField.bind(null, { set: setOccupation, setIsDirty }),
            onSuggestionsFetchRequested: async value => {
                if (value) {
                    const options = await getCreatableLovSuggestions({ type: ELASTICSEARCH_TYPE_OCCUPATION, keywords: value, esToken, setEsToken });
                    setOccupationOptions(options);
                } else setOccupationOptions([]);
            },
        },
        skillset: {
            noOptionsText: NO_OPTIONS_TEXT_SKILLSET,
            options: skillsetOptions,
            placeholder: PLACEHOLDER_FIELD_SKILLSET,
            isMulti: true,
            value: skillset,
            onChange: handleChangeMultiLovField.bind(null, { set: setSkillset, setIsDirty }),
            onSuggestionsFetchRequested: async value => {
                if (value) {
                    const options = await getCreatableLovSuggestions({ type: ELASTICSEARCH_TYPE_SKILL_SET, keywords: value, esToken, setEsToken });
                    setSkillsetOptions(options);
                } else setSkillsetOptions([]);
            },
        },
        education: {
            options: educationLov,
            placeholder: PLACEHOLDER_FIELD_EDUCATION,
            value: education,
            onChange: handleChangeEducation.bind(null, setEducation, setOtherEducation, setIsDirty),
        },
        otherEducation: {
            noOptionsText: NO_OPTIONS_TEXT_OTHER_EDUCATION,
            options: otherEducationOptions,
            placeholder: PLACEHOLDER_FIELD_OTHER_EDUCATION,
            isMulti: true,
            value: otherEducation,
            onChange: handleChangeLovField.bind(null, { set: setOtherEducation, setIsDirty }),
            onSuggestionsFetchRequested: async value => {
                if (value) {
                    const options = await getCreatableLovSuggestions({ type: ELASTICSEARCH_TYPE_OTHER_EDUCATION, keywords: value, esToken, setEsToken });
                    setOtherEducationOptions(options);
                } else setOtherEducationOptions([]);
            },
        },
        aboutMe: {
            multiline: true,
            placeholder: PLACEHOLDER_FIELD_ABOUT_ME,
            inputProps: { maxLength: 99999 },
            value: aboutMe,
            onChange: handleChangeField.bind(null, { set: setAboutMe, setIsDirty }),
        },
        willingRelocateYesButton: {
            onClick: handleClickWillingRelocateButton.bind(null, { value: true, setWillingRelocate, setIsDirty }),
        },
        willingRelocateNoButton: {
            onClick: handleClickWillingRelocateButton.bind(null, { value: false, setWillingRelocate, setIsDirty }),
        },
        // otherEducationContainer: {
        //     className: cx(fieldContainerCls, { [hiddenCls]: otherEducationContainerIsHidden }),
        // },
        pageErrorDialog: {
            message: 'You must enter all required fields.',
            open: pageErrorDialogOpen,
            onClose: () => setPageErrorDialogOpen(false),
        },
        badWordsDialog: {
            title: 'Inappropriate Text is not Allowed!',
            children: (
                <AfitdTypography>
                    The word(s): <b>{badWordsList} </b>
                    does not meet our community guidelines. Inappropriate content cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadWordsDialog,
            onClose: () => clearOutBadWords(),
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleCloseDialog = closeDialog => {
    closeDialog();
};

const handleCloseConfirmClearAllDialog = setIsConfirmClearAllDialogOpen => {
    setIsConfirmClearAllDialogOpen(false);
};

const handleClickConfirmClearAllDialogYes = (clearFields, setIsDirty, setIsConfirmClearAllDialogOpen) => {
    setIsConfirmClearAllDialogOpen(false);
    clearFields();
    setIsDirty(true);
};

const handleClickClearAllButton = setIsConfirmClearAllDialogOpen => {
    setIsConfirmClearAllDialogOpen(true);
};

const handleChangeField = ({ set, setIsDirty, name, invalidRequiredFields, setInvalidRequiredFields }, evt) => {
    set(evt.target.value);
    setIsDirty(true);
    // clear error highlight
    if (invalidRequiredFields) {
        const newInvalidRequiredFields = [...invalidRequiredFields];
        _remove(newInvalidRequiredFields, o => o === name);
        setInvalidRequiredFields(newInvalidRequiredFields);
    }
};

const handleChangeLovField = ({ set, setIsDirty, name, invalidRequiredFields, setInvalidRequiredFields }, evt, option) => {
    set(option);
    setIsDirty(true);
    // clear error highlight
    if (invalidRequiredFields) {
        const newInvalidRequiredFields = [...invalidRequiredFields];
        _remove(newInvalidRequiredFields, o => o === name);
        setInvalidRequiredFields(newInvalidRequiredFields);
    }
};

const handleChangeCurrency = ({ set, setSymbol, setIsDirty, name, invalidRequiredFields, setInvalidRequiredFields }, evt, option) => {
    set(option?.code);
    setSymbol(option?.symbol_native);
    setIsDirty(true);
    // clear error highlight
    if (invalidRequiredFields) {
        const newInvalidRequiredFields = [...invalidRequiredFields];
        _remove(newInvalidRequiredFields, o => o === name);
        setInvalidRequiredFields(newInvalidRequiredFields);
    }
};

const handleChangeMultiLovField = ({ set, setIsDirty, name, invalidRequiredFields, setInvalidRequiredFields }, evt, options) => {
    set(options);
    setIsDirty(true);
    // clear error highlight
    if (invalidRequiredFields) {
        const newInvalidRequiredFields = [...invalidRequiredFields];
        _remove(newInvalidRequiredFields, o => o === name);
        setInvalidRequiredFields(newInvalidRequiredFields);
    }
};

// const handleChangeCountry = (setField, setIsDirty, setState, setCity, setCityLocation, evt, option) => {
//     setField(option?.name);
//     setIsDirty(true);
//     setState(null);
//     setCity(null);
//     setCityLocation(null);
// };

// const handleChangeState = (setField, setIsDirty, setCity, setCityLocation, evt, option) => {
//     setField(option?.name);
//     setIsDirty(true);
//     setCity(null);
//     setCityLocation(null);
// };

// const handleChangeCity = (setField, setIsDirty, evt, option) => {
//     // City is a single-select creatable. Do not extract the name because the option can potentially be:
//     //   { name, location } OR
//     //   { inputValue, name, label }  (service will need inputValue to inserting the new value in db)
//     setField(option);
//     // setCityLocation(option?.location);
//     setIsDirty(true);
// };

const handleChangeEducation = (setEducation, setOtherEducation, setIsDirty, evt, option) => {
    console.log(option);
    setEducation(option);
    if (_get(option, 'value') !== 'OTHER') {
        setOtherEducation([]);
    }
    setIsDirty(true);
};
const handleChangeYearsOfExperience = (setYearsOfExperience, setIsDirty, evt, option) => {
    const minVal = option?.minVal;
    setYearsOfExperience(minVal);
    setIsDirty(true);
};

const handleChangeSalaryRate = (setIsExactRate, setRateMin, setRateMax, setIsDirty, value) => {
    const { isExactRate, rateMin, rateMax } = value;
    setIsExactRate(isExactRate);
    setRateMin(rateMin);
    setRateMax(rateMax);
    setIsDirty(true);
};

const handleClickWillingRelocateButton = ({ value, setWillingRelocate, setIsDirty } = {}) => {
    setWillingRelocate(value);
    setIsDirty(true);
};

const isFormEmpty = (fields = []) => {
    return !_some(fields, item => !_isNil(item) && item !== '');
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AboutMeDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const context = useContext(LayoutContext);
    const userId = _get(context, 'userId');
    const setSnackbarMessage = _get(context, 'setSnackbarMessage');
    const setSnackbarErrorMessage = _get(context, 'setSnackbarErrorMessage');
    const appContext = useContext(AppContext);
    const currentUser = _get(appContext, 'currentUser');
    const lovs = _get(appContext, 'lovs');
    const dialogEl = useRef();

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [isConfirmClearAllDialogOpen, setIsConfirmClearAllDialogOpen] = useState(false);
    const {
        // countryLov,
        // stateLov,
        setStateLov,
        // cityLov,
        setCityLov,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        setCityLocation,
        newCity,
        countryCurrency,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
    } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov', []),
        setIsDirty,
    });
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [gender, setGender] = useState('');
    const [race, setRace] = useState('');
    const [employmentType, setEmploymentType] = useState([]);
    const [yearsOfExperience, setYearsOfExperience] = useState([]);
    const [payFrequency, setPayFrequency] = useState([]);
    const [isExactRate, setIsExactRate] = useState(null);
    const [rateMin, setRateMin] = useState();
    const [rateMax, setRateMax] = useState();
    const [currency, setCurrency] = useState(null);
    const [currencySymbol, setCurrencySymbol] = useState(null);
    const [occupation, setOccupation] = useState([]);
    const [skillset, setSkillset] = useState([]);
    const [education, setEducation] = useState(null);
    const [otherEducation, setOtherEducation] = useState([]);
    const [aboutMe, setAboutMe] = useState('');
    const [salaryRateIsValid, setSalaryRateIsValid] = useState(true);
    const [invalidRequiredFields, setInvalidRequiredFields] = useState();
    const [pageErrorDialogOpen, setPageErrorDialogOpen] = useState(false);
    const [occupationOptions, setOccupationOptions] = useState([]);
    const [skillsetOptions, setSkillsetOptions] = useState([]);
    const [otherEducationOptions, setOtherEducationOptions] = useState([]);
    const [willingRelocate, setWillingRelocate] = useState(null);

    const [openBadWordsDialog, setOpenBadWordsDialog] = useState(false);
    const [badWords, setBadWords] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onClose, open } = props;
    const profileData = _get(props, 'data', {});

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const clearFields = useCallback(() => {
        setFirstName('');
        setMiddleName('');
        setLastName('');
        setGender(null);
        setRace(null);
        setCountry(null);
        setState(null);
        setStateLov([]);
        setCity(null);
        setCityLov([]);
        setCityLocation(null);
        setEmploymentType([]);
        setYearsOfExperience(null);
        setCurrency(null);
        setCurrencySymbol(null);
        setPayFrequency(null);
        setIsExactRate(null);
        setRateMin(null);
        setRateMax(null);
        setOccupation([]);
        setSkillset([]);
        setEducation(null);
        setOtherEducation([]);
        setAboutMe('');
    }, [setCountry, setState, setStateLov, setCity, setCityLov, setCityLocation]);

    const closeDialog = useCallback(() => {
        setIsDirty(false); // reset dirty flag
        clearFields(); // clear all fields when exit
        onClose(); // fire onClose event
    }, [onClose, clearFields]);

    const getPageIsValid = useCallback(() => {
        const requiredFields = { firstName, lastName, gender, race, currency, payFrequency, country, state, city };
        const fieldNames = _keys(requiredFields);
        // let isRequiredFieldsFilled = true;
        const errorFields = [];
        _forEach(fieldNames, fieldName => {
            if (!requiredFields[fieldName]) {
                errorFields.push(fieldName);
            }
        });

        if (!yearsOfExperience && yearsOfExperience !== 0) errorFields.push('yearsOfExperience');

        if (!occupation || _isEmpty(occupation)) errorFields.push('occupation');

        setInvalidRequiredFields(errorFields);

        return errorFields.length === 0 && salaryRateIsValid;
    }, [salaryRateIsValid, firstName, lastName, gender, race, occupation, currency, payFrequency, yearsOfExperience, country, state, city]);

    const handleClickSaveButton = useCallback(async () => {
        const params = {
            userId,
            firstName,
            middleName,
            lastName,
            gender,
            race,
            country,
            state,
            city,
            cityLocation,
            newCity,
            employmentType,
            yearsOfExperience,
            isExactRate,
            rateMin,
            rateMax,
            currency,
            currencySymbol,
            payFrequency,
            occupation,
            skillset,
            education,
            otherEducation,
            aboutMe,
            willingRelocate,
            closeDialog,
            setSnackbarMessage,
            setSnackbarErrorMessage,
        };
        if (getPageIsValid()) {
            setSaveLoading(true);
            console.log({ params });
            let occupation = ',';
            // eslint-disable-next-line no-unused-expressions
            params?.occupation?.forEach(o => {
                if (o?.inputValue) {
                    occupation = `${occupation} ${o?.inputValue}`;
                }
            });

            const data = {
                skillset,
                aboutMe,
                firstName,
                lastName,
                middleName,
                occupation,
                otherEducation,
            };
            const response = await moderateUserTextInput({ text: JSON.stringify(data) });

            if (!response) {
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED);
                setSaveLoading(false);
            } else if (response?.profanity?.length > 0 || response?.personal?.length > 0 || response?.link?.length > 0) {
                setOpenBadWordsDialog(true);
                setBadWords(response);
                setSaveLoading(false);
            } else {
                await saveProfileAboutMe(params);
                setSaveLoading(false);
            }
        } else {
            setSaveLoading(false);
            setPageErrorDialogOpen(true);
        }
    }, [
        userId,
        firstName,
        middleName,
        lastName,
        gender,
        race,
        country,
        state,
        city,
        cityLocation,
        newCity,
        employmentType,
        yearsOfExperience,
        isExactRate,
        rateMin,
        rateMax,
        currency,
        currencySymbol,
        payFrequency,
        occupation,
        skillset,
        education,
        otherEducation,
        aboutMe,
        willingRelocate,
        closeDialog,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        getPageIsValid,
    ]);

    const clearOutBadWords = () => {
        // eslint-disable-next-line no-unused-expressions
        badWords?.profanity?.forEach(badWord => {
            badWord.toLowerCase();

            const regEx = new RegExp(badWord, 'ig');

            if (firstName.toLowerCase().includes(badWord)) {
                const filteredFirstName = firstName.replace(regEx, '');
                setFirstName(filteredFirstName);
            }

            if (middleName.toLowerCase().includes(badWord)) {
                const filteredMiddleName = middleName.replace(regEx, '');
                setMiddleName(filteredMiddleName);
            }

            if (lastName.toLowerCase().includes(badWord)) {
                const filteredLastName = lastName.replace(regEx, '');
                setLastName(filteredLastName);
            }

            if (aboutMe.toLowerCase().includes(badWord)) {
                const filteredAboutMe = aboutMe.replace(regEx, '');
                setAboutMe(filteredAboutMe);
            }

            const filteredOccupation = occupation?.filter(o => {
                // console.log({ o });
                if (o?.inputValue) {
                    const matches = o?.inputValue?.toLowerCase()?.includes(badWord);
                    if (matches) return false;
                    else return true;
                } else return true;
            });
            setOccupation(filteredOccupation);

            const filteredSkillset = skillset?.filter(o => {
                console.log({ o });
                if (o?.inputValue) {
                    const matches = o?.inputValue?.toLowerCase()?.includes(badWord);
                    if (matches) return false;
                    else return true;
                } else return true;
            });
            setSkillset(filteredSkillset);

            const filteredOtherEducation = otherEducation?.filter(o => {
                console.log({ o });
                if (o?.inputValue) {
                    const matches = o?.inputValue?.toLowerCase()?.includes(badWord);
                    if (matches) return false;
                    else return true;
                } else return true;
            });
            setOtherEducation(filteredOtherEducation);
        });
        setOpenBadWordsDialog(false);
        // setBadWords({});
    };

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        if (open === true) {
            setFirstName(_get(currentUser, 'firstName', ''));
            setMiddleName(_get(currentUser, 'middleName', ''));
            setLastName(_get(currentUser, 'lastName', ''));
            setCountry(_get(currentUser, 'country', null));
            setState(_get(currentUser, 'state', null));
            setCity(_get(currentUser, 'city', null));
            setCityLocation(_get(currentUser, 'cityLocation', null));
            setCurrency(_get(currentUser, 'currency', null));
            setCurrencySymbol(_get(currentUser, 'currencySymbol', null));
        }
    }, [currentUser, open, setCountry, setState, setCity, setCityLocation]);

    useEffect(() => {
        if (open === true && !saveLoading) {
            // // name and location fields are now part of the 'users' record (currentUser)
            // setName(_get(profileData, 'name', ''));
            // setCountry(_get(profileData, 'country', null));
            // setState(_get(profileData, 'state', null));
            // setCity(_get(profileData, 'city', null));
            // setCityLocation(_get(profileData, 'cityLocation', null));
            setGender(_get(profileData, 'gender', null));
            setRace(_get(profileData, 'race', null));
            setEmploymentType(_get(profileData, 'employmentType', []));
            setIsExactRate(_get(profileData, 'isExactRate', null));
            setRateMin(_get(profileData, 'rateMin', null));
            setRateMax(_get(profileData, 'rateMax', null));
            setCurrency(_get(profileData, 'currency', null));
            setCurrencySymbol(_get(profileData, 'currencySymbol', null));
            setPayFrequency(_get(profileData, 'payFrequency', null));
            setOccupation(_get(profileData, 'occupation', []));
            setSkillset(_get(profileData, 'skillset', []));
            setEducation(_get(profileData, 'education', null));
            setYearsOfExperience(_get(profileData, 'yearsOfExperience', null));
            setOtherEducation(_get(profileData, 'otherEducation', []));
            setAboutMe(_get(profileData, 'aboutMe', ''));
            setWillingRelocate(_get(profileData, 'willingRelocate', null));
            // console.log('>>>>>>>>>>>>>>>>>>>>> useEffect ', profileData);
        }
    }, [profileData, lovs, open, saveLoading /*setCountry, setState, setCity, setCityLocation*/]);

    // for clearing error in country fields after value
    // useEffect(() => {
    //     if (open === true) getPageIsValid();

    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [country, state, city]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useStyles({ willingRelocate })();
    const defns = getDefns({
        classes,
        props,
        appContext,
        isDirty,
        setIsDirty,
        closeDialog,
        clearFields,
        isConfirmClearAllDialogOpen,
        setIsConfirmClearAllDialogOpen,
        lovs,
        // countryLov,
        // stateLov,
        // cityLov,
        firstName,
        setFirstName,
        middleName,
        setMiddleName,
        lastName,
        setLastName,
        gender,
        setGender,
        race,
        setRace,
        country,
        // setCountry,
        state,
        // setState,
        city,
        // setCity,
        // cityLocation,
        // setCityLocation,
        countryCurrency,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
        employmentType,
        setEmploymentType,
        yearsOfExperience,
        setYearsOfExperience,
        isExactRate,
        setIsExactRate,
        rateMin,
        setRateMin,
        rateMax,
        setRateMax,
        currency,
        setCurrency,
        currencySymbol,
        setCurrencySymbol,
        payFrequency,
        setPayFrequency,
        occupation,
        setOccupation,
        skillset,
        setSkillset,
        education,
        setEducation,
        otherEducation,
        setOtherEducation,
        aboutMe,
        setAboutMe,
        willingRelocate,
        setWillingRelocate,
        handleClickSaveButton,
        setSalaryRateIsValid,
        //getPageIsValid,

        invalidRequiredFields,
        setInvalidRequiredFields,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,

        occupationOptions,
        setOccupationOptions,
        skillsetOptions,
        setSkillsetOptions,
        otherEducationOptions,
        setOtherEducationOptions,
        openBadWordsDialog,
        setOpenBadWordsDialog,
        badWords,
        setBadWords,
        clearOutBadWords,
        saveLoading,
        handleClickWillingRelocateButton,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AfitdEditDialog ref={dialogEl} {...defns.root}>
            <div {...defns.dialogContentContainer}>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>First Name *</DialogFieldLabel>
                    <AfitdTextField {...defns.firstName} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Middle Name</DialogFieldLabel>
                    <AfitdTextField {...defns.middleName} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Last Name *</DialogFieldLabel>
                    <AfitdTextField {...defns.lastName} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabelWithTrackStateInfo>Gender *</DialogFieldLabelWithTrackStateInfo>
                    <AfitdAutocompleteLiteCreatable {...defns.gender} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabelWithTrackStateInfo>Race *</DialogFieldLabelWithTrackStateInfo>
                    <AfitdAutocompleteLite {...defns.race} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Country *</DialogFieldLabel>
                    <AfitdCountryAutocomplete {...defns.country} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>{defns.stateLabel.value} *</DialogFieldLabel>
                    <AfitdStateAutocomplete {...defns.state} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>City *</DialogFieldLabel>
                    <AfitdCityAutocomplete {...defns.city} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Willing to Relocate?</DialogFieldLabel>
                    <AfitdButtonGroup variant="contained" aria-label="contained button group" {...defns.willingRelocateButtonGroup}>
                        <AfitdButton isGroup={true} {...defns.willingRelocateNoButton}>
                            No
                        </AfitdButton>
                        <AfitdButton isGroup={true} {...defns.willingRelocateYesButton}>
                            Yes
                        </AfitdButton>
                    </AfitdButtonGroup>
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Desired Employment Type(s)</DialogFieldLabel>
                    <AfitdAutocompleteLite {...defns.employmentType} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Years of Experience*</DialogFieldLabel>
                    <AfitdYearsOfExperienceAutocomplete {...defns.yearsOfExperience} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Desired Salary/Rate</DialogFieldLabel>
                    <AfitdSalaryRate {...defns.salaryRate} />
                </div>
                <div {...defns.flexFieldContainer}>
                    <div {...defns.fieldContainer}>
                        <DialogFieldLabel>Currency *</DialogFieldLabel>
                        <AfitdCurrencyAutocomplete {...defns.currency} />
                    </div>
                    <div {...defns.fieldContainer}>
                        <DialogFieldLabel>Pay Frequency *</DialogFieldLabel>
                        <AfitdAutocompleteLite {...defns.payFrequency} />
                    </div>
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Desired Occupation *</DialogFieldLabel>
                    <AfitdAutocompleteLiteCreatable {...defns.occupation} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Skillset</DialogFieldLabel>
                    <AfitdAutocompleteLiteCreatable {...defns.skillset} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Education</DialogFieldLabel>
                    <AfitdAutocompleteLite {...defns.education} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>Other Education, License(s), or Certification(s)</DialogFieldLabel>
                    <AfitdAutocompleteLiteCreatable {...defns.otherEducation} />
                </div>
                <div {...defns.fieldContainer}>
                    <DialogFieldLabel>About Me</DialogFieldLabel>
                    <AfitdTextField {...defns.aboutMe} />
                </div>
            </div>
            <Dialog aria-labelledby="confirm-clear-all-dialog" aria-describedby="confirm-clear-all-dialog" {...defns.confirmClearAllDialog}>
                <DialogTitle id="alert-dialog-title">Clear All?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <AfitdTypography variant="body1">All unsaved changes will be lost.</AfitdTypography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <AfitdButton {...defns.confirmClearAllDialogCancelButton}>No</AfitdButton>
                    <AfitdButton {...defns.confirmClearAllDialogOkButton}>Yes</AfitdButton>
                </DialogActions>
            </Dialog>
            <AfitdMessageDialog {...defns.pageErrorDialog} />
            <AfitdMessageDialog {...defns.badWordsDialog} />
        </AfitdEditDialog>
    );
};

export default AboutMeDialog;
